import type { CountryTaxConfig } from "@/api/countryTaxConfig";
import type { DeclaredVia, TaxDeclarationInput } from "@/api/taxDeclaration";
import { formatRatePercent } from "@/shared/utils/percent";

/** What the creator enters. There is no rate field: the rate comes from the country configuration (Ticket-348). */
export type TaxDetailsValues = {
	countryCode: string;
	entityType: "individual" | "company" | "";
	legalName: string;
	isTaxRegistered: boolean;
	registrationNumber: string;
};

export type TaxDetailsErrors = Partial<
	Record<"countryCode" | "entityType" | "legalName" | "registrationChoice" | "registrationNumber", string>
>;

export const EMPTY_TAX_DETAILS: TaxDetailsValues = {
	countryCode: "",
	entityType: "",
	legalName: "",
	isTaxRegistered: false,
	registrationNumber: "",
};

/** The backend strips whitespace before testing the pattern; mirror it so "12 345 678 901" passes. */
export const normaliseRegistrationNumber = (value: string): string => value.replace(/\s+/g, "");

type PatternConfig = Pick<CountryTaxConfig, "registrationNumberLabel" | "registrationNumberPattern"> | null;

/** Client-side format checks use the backend's country configuration. */
export function validateTaxDetails(values: TaxDetailsValues, config: PatternConfig): TaxDetailsErrors {
	const errors: TaxDetailsErrors = {};
	if (!values.countryCode.trim()) errors.countryCode = "Country of tax residence is required.";
	if (values.entityType !== "individual" && values.entityType !== "company")
		errors.entityType = "Choose individual or company.";
	if (!values.legalName.trim()) errors.legalName = "Legal name is required.";
	if (values.isTaxRegistered) {
		const number = normaliseRegistrationNumber(values.registrationNumber);
		const label = config?.registrationNumberLabel ?? "Tax registration number";
		const pattern = config?.registrationNumberPattern ? new RegExp(config.registrationNumberPattern) : null;
		if (!number) errors.registrationNumber = `${label} is required when registered.`;
		else if (pattern && !pattern.test(number)) {
			errors.registrationNumber = `That does not look like a valid ${label}.`;
		}
	}
	return errors;
}

export { formatRatePercent };

/** The read-only line under the fields. */
export function rateLine(config: CountryTaxConfig | null, countryName: string): string {
	if (!config)
		return `Payment Statements are not issued for ${countryName} yet. Your tax details are saved now and used once they are.`;
	if (config.effectiveRate === null || config.effectiveRate === 0) {
		return `No ${config.taxName} is added to Payment Statements for ${countryName}.`;
	}
	return `${config.taxName} in ${countryName} is currently ${formatRatePercent(config.effectiveRate)}. It is added on top of your fee on each Payment Statement at the rate in force on the day the statement is issued.`;
}

export function toDeclarationInput(values: TaxDetailsValues, declaredVia: DeclaredVia): TaxDeclarationInput {
	return {
		countryCode: values.countryCode.trim().toUpperCase(),
		entityType: values.entityType === "company" ? "company" : "individual",
		legalName: values.legalName.trim(),
		isTaxRegistered: values.isTaxRegistered,
		registrationNumber: values.isTaxRegistered ? normaliseRegistrationNumber(values.registrationNumber) : null,
		declaredVia,
	};
}
